import {
  DatePicker,
  Field,
  FieldLabel,
  LeaderGroupCombobox,
  ModuleWrapper,
  UserCombobox,
} from '@/components'
import { Card } from '@/components_deprecated/Card'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import {
  asNullableString,
  asString,
  asStringArrayOrEmpty,
  parseDateRangeOrDefault,
} from '@/utils'
import { InfringementStatuses } from '../consts'
import type { InfringementStatus } from '../types'
import {
  useAdmin,
  useLeaderGroups,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { useMemo } from 'react'
import { PERMISSIONS } from '@/utils/permissions'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, startOfMonth } from 'date-fns'
import { isDesktop } from 'react-device-detect'
import styled from 'styled-components'
import { InfringementPopupFilter } from '../components'
import { InfringementsTable } from '../components/table/InfringementsTable'
import type { InfringementsTableFilter, SeverityCategory } from '../types'
import type { InfringementCategory } from '../types.graphl'
import { useAutoSelectSingleLeaderGroup } from '@/utils/hooks'

const FilterSection = styled.section`
  grid-area: filters;

  display: flex;
  gap: 0.75rem;

  & > * {
    max-width: 100%;
  }
`

const Controls = styled(Card)`
  width: 100%;
  box-sizing: border-box;
  overflow-x: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  display: flex;
`

const DEFAULT_DATE_RANGE = {
  from: startOfMonth(new Date()),
  to: endOfMonth(new Date()),
}

interface InfringementsProps {
  title?: string
  module?: Module
  storageName?: string
  detailPathBase?: string
}

export const Infringements: React.FC<InfringementsProps> = ({
  title,
  module = Module.INFRINGEMENTS,
  storageName = 'infringement-filters',
  detailPathBase = '/infringements',
}) => {
  const translations = useTranslate({
    infringements: 'common.infringements',
    archived: 'common.archived',

    users: 'infringements.filters.users',
    leaderGroup: 'infringements.filters.leader-group',
    dateRange: 'infringements.filters.date-range',
    advancedFilters: 'infringements.filters.advanced-filters',

    select: 'common.select-ellipsis',

    success: 'toasts.email-sent-successfully',
    error: 'errors.generic-error',
  })

  const admin = useAdmin()
  const me = useUser()
  const { hasPermissions } = usePermissions()

  const { myLeaderGroups, groupsLoading } = useLeaderGroups(module)
  const hasLeaderGroups = myLeaderGroups.length > 0

  const canViewOthers = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement,
    true
  )

  const {
    formValues: filter,
    formChangeHandler: filterHandler,
    updateForm,
    loadStoredFormState,
  } = useForm<InfringementsTableFilter>({
    values: {
      query: '',
      orderBy: '-infringementTimestamp',
      userOrderBy: '-firstName',
      excludeSeverities: [],
      excludeCategories: [],
      statuses: ['OPEN'],
      users: canViewOthers ? [] : [me.id],
      dateRange: DEFAULT_DATE_RANGE,
      leaderGroup: null,
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: storageName,
        sessionStorage: true,
        transformValues: {
          orderBy: asString,
          userOrderBy: asString,
          statuses: value => {
            const allowed = new Set<InfringementStatus>(
              Object.keys(InfringementStatuses) as InfringementStatus[]
            )
            const arr = asStringArrayOrEmpty(value).filter(
              (item): item is InfringementStatus =>
                allowed.has(item as InfringementStatus)
            )
            return arr.length > 0 ? arr : ['OPEN']
          },
          users: asStringArrayOrEmpty,
          dateRange: parseDateRangeOrDefault,
          leaderGroup: asNullableString,
          excludeSeverities: value => {
            const severities = new Set<SeverityCategory>([
              'MINOR',
              'SERIOUS',
              'VERY_SERIOUS',
              'MOST_SERIOUS',
            ])
            return asStringArrayOrEmpty(value).filter(
              (item): item is SeverityCategory =>
                severities.has(item as SeverityCategory)
            )
          },
          excludeCategories: value => {
            return asStringArrayOrEmpty(value) as InfringementCategory[]
          },
        },
      },
    },
  })

  const selectedLeaderGroupUsers = useMemo(
    () =>
      new Set(
        myLeaderGroups
          .find(group => group.id === filter.leaderGroup)
          ?.users.map(user => user.id) ?? []
      ),
    [filter.leaderGroup, myLeaderGroups]
  )

  const handleLeaderGroupChange = (value: string) => {
    updateForm({
      leaderGroup: value || null,
      users: [],
    })
  }

  useAutoSelectSingleLeaderGroup({
    canViewOthers: canViewOthers && hasLeaderGroups && !groupsLoading,
    isAdmin: admin,
    leaderGroup: filter.leaderGroup ?? undefined,
    leaderGroups: myLeaderGroups,
    loadStoredFormState,
    updateForm,
    onAutoSelect: group => {
      updateForm({ leaderGroup: group.id })
    },
  })

  return (
    <ModuleWrapper
      title={
        title ??
        (isDesktop
          ? `${translations.infringements}${
              filter.statuses.length === 1 && filter.statuses[0] === 'ARCHIVED'
                ? ' (' + translations.archived + ')'
                : ''
            }`
          : translations.infringements)
      }
      page={PageType.INFRINGEMENTS}
    >
      <Controls>
        <FilterSection className="flex-wrap">
          {isDesktop && canViewOthers && (
            <Field>
              <FieldLabel>{translations.users}</FieldLabel>
              <UserCombobox
                value={filter.users}
                mode="multiple"
                className="min-w-[260px] max-w-full"
                placeholder={translations.select}
                onChange={value => updateForm({ users: value })}
                hideItem={item =>
                  !!filter.leaderGroup && !selectedLeaderGroupUsers.has(item.id)
                }
              />
            </Field>
          )}

          {hasLeaderGroups && canViewOthers && (
            <Field>
              <FieldLabel>{translations.leaderGroup}</FieldLabel>
              <LeaderGroupCombobox
                mode="single"
                module={module}
                value={filter.leaderGroup ?? ''}
                clearable
                placeholder={translations.select}
                onChange={val => {
                  handleLeaderGroupChange(val)
                }}
              />
            </Field>
          )}

          <Field>
            <FieldLabel>{translations.dateRange}</FieldLabel>
            <DatePicker
              mode="range"
              value={filter.dateRange}
              format="PP"
              onChange={value => {
                if (!value?.from || !value.to) return
                filterHandler('dateRange')({ from: value.from, to: value.to })
              }}
            />
          </Field>

          <Field>
            <FieldLabel>{translations.advancedFilters}</FieldLabel>
            <InfringementPopupFilter filter={filter} onUpdate={updateForm} />
          </Field>
        </FilterSection>
      </Controls>

      <InfringementsTable
        filter={filter}
        detailPathBase={detailPathBase}
        onSort={filterHandler('orderBy')}
      />
    </ModuleWrapper>
  )
}
